import { useEffect, useRef, useState } from 'preact/hooks';
import * as st from '../state/store';
import { canAccess } from '../entitlements/tier';

const SCHOOL_BUNDLE = '/downloads/marrow-offline-site.zip';
import { track } from '../analytics';
import { download, fetchManifest, mb, plan, readState, remove, usage, type OfflineManifest, type OfflineState } from '../pwa/offline';

type Status = { kind: 'idle' | 'working' | 'done' | 'error'; text?: string; done?: number; total?: number };

/** "Use offline" (F12): full download for supporters and schools, storage display, removal. */
export function OfflinePanel() {
  const entitled = canAccess('supporter');
  const [manifest, setManifest] = useState<OfflineManifest | null>(null);
  const [state, setState] = useState<OfflineState | null>(null);
  const [used, setUsed] = useState<{ usage: number; quota: number } | null>(null);
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const first = useRef<HTMLButtonElement>(null);
  const close = () => (st.offlineOpen.value = false);

  const refresh = async () => {
    setState(await readState().catch(() => null));
    setUsed(await usage());
  };
  useEffect(() => {
    first.current?.focus();
    fetchManifest().then(setManifest).catch(() => setManifest(null));
    void refresh();
  }, []);

  const todo = manifest ? plan(manifest, state) : null;
  const complete = !!state?.version && !!todo && todo.fetch.length === 0;

  const start = async () => {
    setStatus({ kind: 'working', done: 0, total: todo?.bytes ?? 0 });
    track('offline_download', { phase: 'start' });
    const r = await download({ manifest: manifest ?? undefined, onProgress: (done, total) => setStatus({ kind: 'working', done, total }) }).catch(
      (e: unknown) => ({ ok: false as const, reason: 'network' as const, fetched: 0, message: `The download couldn’t start (${(e as Error).message}).` }),
    );
    await refresh();
    if (r.ok) {
      track('offline_download', { phase: 'complete' });
      setStatus({ kind: 'done', text: 'Done. The whole atlas now works without a connection.' });
    } else setStatus({ kind: 'error', text: r.message });
  };

  const drop = async () => {
    await remove();
    await refresh();
    setStatus({ kind: 'idle', text: 'Offline copy removed.' });
  };

  const pct = status.kind === 'working' && status.total ? Math.round(((status.done ?? 0) / status.total) * 100) : 0;
  return (
    <div class="overlay" onClick={(e) => e.target === e.currentTarget && close()} onKeyDown={(e) => e.key === 'Escape' && (e.stopPropagation(), close())}>
      <div class="dialog offline-dialog" role="dialog" aria-modal="true" aria-labelledby="offline-title" data-testid="offline-panel">
        <div class="dialog-head">
          <h2 id="offline-title">Use offline</h2>
          <button ref={first} type="button" class="btn ghost icon" aria-label="Close" onClick={close}>✕</button>
        </div>
        <p>Parts and cards you’ve already opened keep working without a connection.</p>
        {entitled ? (
          <>
            <p>
              Download everything (all body systems, every card and tour) so the whole atlas works on a plane or in a classroom with no Wi-Fi.
              {manifest && <> About <strong>{mb(manifest.bytes)}</strong>.</>}
            </p>
            <div class="dialog-actions">
              {complete ? (
                <span class="ok" data-testid="offline-complete">✓ Downloaded</span>
              ) : (
                <button type="button" class="btn primary" onClick={start} disabled={status.kind === 'working' || !manifest} data-testid="offline-download">
                  {status.kind === 'working' ? `Downloading… ${pct}%` : state?.version ? `Update (${mb(todo?.bytes ?? 0)})` : 'Download everything'}
                </button>
              )}
              {state && (
                <button type="button" class="btn ghost" onClick={drop} disabled={status.kind === 'working'} data-testid="offline-remove">Remove offline data</button>
              )}
            </div>
            {canAccess('school') && (
              <p data-testid="school-bundle">
                <strong>For your school network:</strong> <a href={SCHOOL_BUNDLE} download>download the whole site as a zip</a> to serve from a computer on your network or a USB stick, with no internet. Instructions are inside.
              </p>
            )}
            {status.kind === 'working' && <progress max={100} value={pct} aria-label="Download progress" data-testid="offline-progress" />}
          </>
        ) : (
          <p data-testid="offline-locked">
            Downloading the whole atlas is a supporter perk.{' '}
            <button type="button" class="link" onClick={() => ((st.panel.value = 'unlock'), close())} data-testid="offline-unlock">Become a supporter</button>
            . The 3D models themselves stay free to download from the Credits page.
          </p>
        )}
        <p class={`status ${status.kind === 'error' ? 'error' : ''}`} role="status" aria-live="polite" data-testid="offline-status">{status.text ?? ''}</p>
        {used && <p class="fine" data-testid="offline-usage">This site is using {mb(used.usage)} of storage on this device.</p>}
        <p class="fine">On iPhone and iPad, Safari may clear offline data after a few weeks without a visit. If that happens, download again.</p>
      </div>
    </div>
  );
}
